/*** Environment ***/
import { Environment } from '../environments/environment';

/*** App Components ***/

// General
import { AppComponent } from './app.component';
import { PortalComponent } from './portal/portal.component';
import { LicensingComponent } from './licensing/licensing.component';
import { LoginComponent } from './login/login.component';
import { NavComponent } from './nav/nav.component';
import { RegisterComponent } from './register/register.component';
import { DialogComponent } from './_services/dialog.service';
import { ToolkitComponent } from './toolkit-page/toolkit.component';
import { HelpComponent } from './help-page/help.component';
import { TosComponent } from './tos/tos.component';
import { NotFoundComponent } from './_shared/not-found.component';
import { ProfileComponent } from './profile/profile.component';
import { PasswordResetComponent } from './password-reset/password-reset.component';

// UI Elements
import { ChunkyCheckBoxComponent } from './_shared/check-box/check-box.component';
import { FileUploadComponentComponent } from './file-upload-component/file-upload-component.component';
import { IphoneFrameComponent } from './iphone-frame/iphone-frame.component';
import { Mp3VolumeComponent } from './mp3-slider/mp3-volume/mp3-volume.component';
import { Mp3PlayerComponent } from './mp3-slider/mp3-player.component';
import { ValidationDisplayComponent } from './validation-display/validation-display.component';
import { CouponCodeComponent } from './coupon-code/coupon-code.component';
import { ErrorTooltipComponent } from './lightshow-preview/Components/error-tooltip.component';
import { ColorInputComponent } from './lightshow-preview/Components/smart-color/color-input/color-input.component';
import { ServicePeriodComponent } from './service-period/service-period.component';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatExpansionModule } from '@angular/material/expansion';

// Admin
import { UserActionsModalComponent } from './user-management/user-actions-modal.component';
import { AdministrationComponent } from './administration/admin.component';
import { PaymentHistoryComponent } from './administration/payment-history.component';
import { ClientTypeCreateModalComponent } from './administration/client-type-create-modal.component';
import { PurchaseDetailsModalComponent } from './administration/purchase-details-modal.component';
import { EngineCreateModalComponent } from './administration/engine-create-modal.component';
import { EngineManagementComponent } from './administration/engine-management.component';
import { OrganizationsComponent } from './administration/organizations/organizations.component';
import { OrganizationsFormComponent } from './administration/organizations/organizations-form/organizations-form.component';
import { ClientWizardComponent } from './administration/client-wizard/client-wizard.component';
import { ImageUploadModalComponent } from './administration/image-upload-modal.component';
import { ImageUploadManagementComponent } from './administration/image-upload-management.component';
import { EngineStatsModalComponent } from './administration/engine-stats-modal.component';
import { ConfigManagementComponent } from './administration/engine-config-management.component';
import { ConfigCreateModalComponent } from './administration/engine-config-create-modal.component';
import { MerchantCreateComponent } from './fundraising/admin/merchant/merchant-create.component';
import { MerchantManagementComponent } from './fundraising/admin/merchant/merchant-management.component';
import { VendorCreateComponent } from './fundraising/admin/vendor/vendor-create.component';
import { VendorManagementComponent } from './fundraising/admin/vendor/vendor-management.component';
import { FundraisingAccountLinkModalComponent } from './fundraising/admin/fundraising-account-link-modal.component';
import { UserCreateComponent } from './user-management/user-create.component';
import { UserManagementComponent } from './user-management/user-management.component';
import { StationAppUploadModalComponent } from './fundraising/admin/station-app/station-app-upload-modal.component';
import { StationAppManagementComponent } from './fundraising/admin/station-app/station-app-management.component';
import { JsonVisualEditorComponent } from './json-visual-editor/json-visual-editor.component';
import { AppConfigManagementComponent } from './administration/app-config-management.component';
import { AppConfigCreateModalComponent } from './administration/app-config-create-modal.component';
import { EngineStatusModalComponent } from './administration/engine-status-modal.component';

// Live Events
import { LightShowManagementComponent } from './light-show-management/light-show-management.component';
import { SelfieCamComponent } from './selfie-cam/selfie-cam.component';
import { LightShowCreateComponent } from './light-show-management/light-show-create.component';
import { AudioPlayerComponent } from './audio-player/audio-player.component';
import { SongControlComponent } from './songs/song-control/song-control.component';
import { TrackLibraryComponent } from './songs/track-library/track-library.component';
import { SelfieCamFilterGeneratorComponent } from './selfie-cam/selfie-cam-filter-generator.component';
import { TriviaComponent } from './trivia/trivia.component';
import { SongsComponent } from './songs/songs.component';
import { SelfieCamService } from './_services/selfie-cam.service';
import { SelfieCamViewComponent } from './selfie-cam/selfie-cam-view.component';
import { TriviaService } from './_services/trivia-service';
import { SelfieCamManagementComponent } from './selfie-cam/selfie-cam-management.component';
import { TriviaManagementComponent } from './trivia/trivia-management.component';
import { LightshowPreviewComponent } from './lightshow-preview/lightshow-preview.component';
import { SoundEffectService } from './_services/sound-effect.service';
import { TrackCreateModalComponent } from './songs/track-library/track-create-modal.component';
import { CouponsComponent } from './coupons/coupons.component';
import { CouponFormComponent } from './coupons/coupon-form/coupon-form.component';
import { CouponSelectComponent } from './coupons/coupon-select/coupon-select.component';
import { UpnFormComponent } from './upn/upn-form/upn-form.component';
import { UpnComponent } from './upn/upn.component';
import { SoundEffectPlayerComponent } from './lightshow-preview/Components/sound-effect-player.component';
import { FullscreenPreviewComponent } from './lightshow-preview/Components/fullscreen-preview.component';
import { PreviewComponent } from './lightshow-preview/Components/preview.component';
import { CoreEngineManagementComponent } from './core-engine/core-engine.component';
import { CoreEngineService } from './_services/core-engine.service';
import { SmartColorFormComponent } from './lightshow-preview/Components/smart-color/smart-color-form.component';
import { CoreEngineFormComponent } from './core-engine/core-engine-form/core-engine.component';
import { GenreEditModalComponent } from './songs/track-library/genre-edit-modal.component';
import { CoreEngineFieldComponent } from './core-engine/core-engine-form/core-engine-field.component';
import { LightshowTorchEditorComponent } from './lightshow-preview/lightshow-torch-editor/lightshow-torch-editor.component';
import { LightshowTorchInstructionsComponent } from './lightshow-preview/lightshow-torch-instructions/lightshow-torch-instructions.component';
import { TriviaPlayersComponent } from './trivia/trivia-results/trivia-players.component';
import { TriviaResultsComponent } from './trivia/trivia-results/trivia-results.component';
import { MobileAppStoreRedirectComponent } from './mobile-app-store-redirect/mobile-app-store-redirect.component';
import { VenueManagementComponent } from './venue-editor/mangement/venue-mangement.component';
import { VenueFormComponent } from './venue-editor/form/venue-form.component';
import { NetworkSyncModalComponent } from './network-sync/networksync-modal.component';
import { NetworkSyncService } from './_services/network-sync.service';
import { ScheduledEventComponent } from './scheduled-events/scheduled-events.component';
import { NetworkSyncComponent } from './network-sync/networksync.component';
import { TrackMetadataModalComponent } from './songs/track-library/track-metadata-modal.component';
import { ScreenStrobeFormComponent } from './lightshow-preview/Components/strobe-color/screen-strobe-form.component';
import { CouponPreviewModalComponent } from './coupons/coupon-form/coupon-preview-modal.component';
import { VenueSectionInputModalComponent } from './venue-editor/form/venue-section-input.component';
import { LightShowPublicPreviewComponent } from './lightshow-public-preview/lightshow-public-preview.component';
import { cueServicesDbConfig } from './_shared/base.cueservice.component';

// Fundraising
import { FundraisingManagementComponent } from './fundraising/fundraising-management.component';
import { MerchantLoginComponent } from './fundraising/merchant/merchant-login.component';
import { Cue5050CreateComponent } from './fundraising/cue5050/cue5050-create.component';
import { StationManagementComponent } from './fundraising/station/station-management.component';
import { OperatorManagementComponent } from './fundraising/operator/operator-management.component';
import { LocationManagementComponent } from './fundraising/location/location-management.component';
import { LocationFormComponent } from './fundraising/location/location-form.component';
import { LocationCreateComponent } from './fundraising/location/location-create.component'
import { VendorService } from './_services/fundraising/vendor.service';
import { MerchantPortalComponent } from './fundraising/merchant/merchant-portal.component';
import { OperatorCreateComponent } from './fundraising/operator/operator-create.component';
import { StationCreateComponent } from './fundraising/station/station-create.component';
import { OperatorStationLinkModalComponent } from './fundraising/operator/operator-station-link-modal.component';
import { DeviceInfoModalComponent } from './fundraising/station/device-info-modal.component';
import { VendorWebsiteCreateComponent } from './fundraising/website/vendor-website-create.component';
import { ChooseWinningTicketModalComponent } from './fundraising/cue5050/choose-winning-ticket-modal.component';
import { VerifyTicketModalComponent } from './fundraising/cue5050/verify-winning-ticket-modal.component';
import { EventPotSizeComponentComponent } from './fundraising/event-pot-size-component/event-pot-size-component.component';
import { ReportsLandingPageComponent } from './fundraising/reports/reports-landing-page.component';
import { EventDetailsModalComponent } from './fundraising/cue5050/event-details-modal.component';
import { PledgePerWinManagementComponent } from './fundraising/ppw/ppw-management.component';
import { PledgePerWinCreateComponent } from './fundraising/ppw/ppw-create.component';
import { BarGraphReportComponent } from './fundraising/reports/bar-graph-report.component';
import { BarGraphComponent } from './fundraising/reports/graph/bar-graph.component';
import { BarComponent } from './fundraising/reports/graph/bar.component';
import { SavedReportsComponent } from './fundraising/reports/saved-reports.component';
import { SavedReportNamingModalComponent } from './fundraising/reports/saved-report-naming-modal.component';
import { HeatmapReportComponent } from './fundraising/reports/heatmap/heatmap-report.component';
import { PledgePerWinDetailsModalComponent } from './fundraising/ppw/ppw-details-modal-component';
import { PledgePerWinGameCompletionModalComponent } from './fundraising/ppw/games/ppw-game-completion-modal.component';
import { PledgePerWinGameConfirmModalComponent } from './fundraising/ppw/games/ppw-game-confirm-modal.component';
import { TeamManagementComponent } from './fundraising/ppw/teams/team-management.component';
import { TeamCreateComponent } from './fundraising/ppw/teams/team-create.component';
import { PrinterManagementComponent } from './fundraising/printers/printer-management.component';
import { PrinterCreateComponent } from './fundraising/printers/printer-create.component';
import { PledgePerWinGameManagementComponent } from './fundraising/ppw/games/ppw-game-management.component';
import { PledgePerWinSortOrderModalComponent } from './fundraising/ppw/ppw-sort-order-modal.component';
import { PledgePerWinSubscribersManagementComponent } from './fundraising/ppw/subscribers/ppw-subscribers-management.component';
import { PledgePerWinSubscriberDetailsModalComponent } from './fundraising/ppw/subscribers/ppw-subscribers-details-modal.component'; 
import { PledgePerWinCancelModalComponent } from './fundraising/ppw/subscribers/ppw-cancel-reverse-modal.component';
import { PledgePerWinWebsiteCreateComponent } from './fundraising/ppw/website/ppw-website-create.component';
import { VendorUserCreateComponent } from './fundraising/admin/vendor-user/vendor-user-create.component';
import { PledgePerWinPayoutManagementComponent } from './fundraising/ppw/ppw-payout-management.component';

/*** Directives ***/
import { RequiredIfDirective } from './_directives/required-if.directive';
import { IgnoreWhiteSpaceDirective } from './_directives/ignore-whitespace.directive';
import { DebounceClickDirective } from './_directives/debounce.directive';

/*** Modules ***/
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, NO_ERRORS_SCHEMA, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { NgxSpinnerModule } from 'ngx-spinner';
import { NgSelectModule } from '@ng-select/ng-select';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ClipboardModule } from 'ngx-clipboard';
import { ModalDialogModule } from '@preeco-privacy/ngx-modal-dialog';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { FileUploadModule } from 'ng2-file-upload';
import { AngularMyDatePickerModule } from 'angular-mydatepicker';
import { RouterModule } from '@angular/router';
import { HttpClientModule, HTTP_INTERCEPTORS  } from '@angular/common/http';
import { Ng2ImgMaxModule } from './_modules/ng2-img-max.module';
import { ColorPickerModule } from './_modules/color-picker.module';
import { DndModule } from 'ngx-drag-drop';
import { NgxIndexedDBModule } from 'ngx-indexed-db';

/*** Routing ***/
import { AppRoutes } from './routes';
import { DebugHttpInterceptor } from './_services/interceptor.service';

/*** Pipes ***/
import { SecondsToTimePipe } from './filters/seconds-to-time-filter';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { OrderModule } from 'ngx-order-pipe';

/*** Guards ***/
import { CanDeactivateGuard } from './_services/can-deactivate-guard.service';
import { UserManagementGuard, AdministrationGuard } from './_guards/user.guard';
import { AuthGuard, MerchantAuthGuard } from './_guards/auth.guard';
import { ReverseAuthGuard, ReverseMerchantAuthGuard } from './_guards/reverse-auth.guard';
import { 
  LightShowGuard, SelfieCamGuard, TriviaGuard, CoreEngineGuard, UpnGuard, CouponGuard, 
  Cue5050Guard, PledgePerWinGuard
} from './_guards/service.guard';

/*** Services ***/
import { AuthService } from './_services/auth.service';
import { LightShowTrackService } from './_services/track.service';
import { PhotoService } from './_services/photo.service';
import { VideoService } from './_services/video.service';
import { LightShowService } from './_services/light-show.service';
import { UserService } from './_services/user.service';
import { OrganizationService } from './_services/organization.service';
import { MessageService } from './_services/message.service';
import { AudioPlayerService } from './_services/audio-player.service';
import { S3Service } from './_services/s3.service';
import { PaymentService } from './_services/payment-service.service';
import { ProductService } from './_services/product.service';
import { EngineService } from './_services/engine.service';
import { VenueService } from './_services/venue.service';
import { ScheduledEventService } from './_services/scheduled-event.service';
import { UPNService } from './_services/upn.service';
import { JWTTokenService } from './_services/jwtToken.service';
import { MerchantService } from './_services/fundraising/merchant.service';
import { FundraisingService } from './_services/fundraising/fundraising.service';
import { Cue5050Service } from './_services/fundraising/cue5050.service';
import { StationService } from './_services/fundraising/station.service';
import { OperatorService } from './_services/fundraising/operator.service';
import { AudioTrackStoreService } from './_services/audio-track-store.service';
import { DialogService } from './_services/dialog.service';
import { SentryErrorHandler } from './_services/sentry.service';
import { CouponService } from './_services/coupon.service';
import { Cue5050SiteService } from './_services/fundraising/cue5050Site.service';
import { TicketHistoryManagementComponent } from './fundraising/ticket-history-management/ticket-history-management.component';
import { QRCodeService } from './_services/fundraising/qrCode.service';
import { FundraisingReportService } from './_services/fundraising/fundraisingReport.service';
import { PledgePerWinService } from './_services/fundraising/pledgePerWin.service';
import { PrinterService } from './_services/fundraising/printer.service';
import { PledgePerWinSiteService } from './_services/fundraising/pledgePerWinSite.service';
import { InactivityMonitorService } from './_services/inactivity-monitor.service';
import { CueAppService } from './_services/cueApp.service';
import { FileDownloadService } from './_services/file-download.service';

@NgModule({
  declarations: [
    AppComponent,
    LicensingComponent,
    LightShowManagementComponent,
    LightShowCreateComponent,
    LoginComponent,
    NavComponent,
    RegisterComponent,
    ProfileComponent,
    PasswordResetComponent,
    UserManagementComponent,
    UserCreateComponent,
    AudioPlayerComponent,
    SongControlComponent,
    SelfieCamComponent,
    SelfieCamViewComponent,
    SelfieCamManagementComponent,
    TriviaPlayersComponent,
    TrackLibraryComponent,
    RequiredIfDirective,
    IgnoreWhiteSpaceDirective,
    DebounceClickDirective,
    DialogComponent,
    PortalComponent,
    Mp3PlayerComponent,
    SecondsToTimePipe,
    SelfieCamFilterGeneratorComponent,
    ClientTypeCreateModalComponent,
    TrackCreateModalComponent,
    GenreEditModalComponent,
    PurchaseDetailsModalComponent,
    EngineCreateModalComponent,
    ConfigCreateModalComponent,
    FileUploadComponentComponent,
    ServicePeriodComponent,
    TriviaComponent,
    CoreEngineManagementComponent,
    CoreEngineFormComponent,
    CouponsComponent,
    CouponFormComponent,
    OrganizationsComponent,
    OrganizationsFormComponent,
    UpnComponent,
    UpnFormComponent,
    IphoneFrameComponent,
    SongsComponent,
    TriviaManagementComponent,
    CouponCodeComponent,
    HelpComponent,
    ToolkitComponent,
    TosComponent,
    LightshowPreviewComponent,
    AdministrationComponent,
    ValidationDisplayComponent,
    PaymentHistoryComponent,
    EngineManagementComponent,
    SmartColorFormComponent,
    ColorInputComponent,
    SoundEffectPlayerComponent,
    FullscreenPreviewComponent,
    PreviewComponent,
    Mp3VolumeComponent,
    ErrorTooltipComponent,
    NotFoundComponent,
    CoreEngineManagementComponent,
    CouponSelectComponent,
    CoreEngineFieldComponent,
    LightshowTorchEditorComponent,
    LightshowTorchInstructionsComponent,
    ClientWizardComponent,
    UserActionsModalComponent,
    EngineStatsModalComponent,
    TriviaResultsComponent,
    ConfigManagementComponent,
    MobileAppStoreRedirectComponent,
    VenueManagementComponent,
    VenueFormComponent,
    NetworkSyncModalComponent,
    NetworkSyncComponent,
    ScheduledEventComponent,
    FundraisingManagementComponent,
    MerchantManagementComponent,
    MerchantCreateComponent,
    MerchantLoginComponent,
    VendorManagementComponent,
    VendorCreateComponent,
    ImageUploadModalComponent,
    ImageUploadManagementComponent,
    MerchantPortalComponent,
    FundraisingAccountLinkModalComponent,
    ChunkyCheckBoxComponent,
    Cue5050CreateComponent,
    StationManagementComponent,
    OperatorManagementComponent,
    LocationManagementComponent,
    LocationFormComponent,
    LocationCreateComponent,
    OperatorCreateComponent,
    StationCreateComponent,
    OperatorStationLinkModalComponent,
    DeviceInfoModalComponent,
    VendorWebsiteCreateComponent,
    ChooseWinningTicketModalComponent,
    VerifyTicketModalComponent,
    EventPotSizeComponentComponent,
    TicketHistoryManagementComponent,
    TrackMetadataModalComponent,
    StationAppUploadModalComponent,
    StationAppManagementComponent,
    EventDetailsModalComponent,
    PledgePerWinManagementComponent,
    PledgePerWinCreateComponent,
    ReportsLandingPageComponent,
    EventDetailsModalComponent,
    BarGraphReportComponent,
    BarGraphComponent,
    BarComponent,
    SavedReportsComponent,
    SavedReportNamingModalComponent,
    HeatmapReportComponent,
    PledgePerWinDetailsModalComponent,
    PledgePerWinGameCompletionModalComponent,
    PledgePerWinGameConfirmModalComponent,
    TeamManagementComponent,
    TeamCreateComponent,
    PrinterManagementComponent,
    PrinterCreateComponent,
    ScreenStrobeFormComponent,
    PledgePerWinGameManagementComponent,
    PledgePerWinSortOrderModalComponent,
    PledgePerWinSubscribersManagementComponent,
    PledgePerWinSubscriberDetailsModalComponent,
    PledgePerWinCancelModalComponent,
    PledgePerWinWebsiteCreateComponent,
    CouponPreviewModalComponent,
    VendorUserCreateComponent,
    VenueSectionInputModalComponent,
    PledgePerWinPayoutManagementComponent,
    JsonVisualEditorComponent,
    AppConfigManagementComponent,
    AppConfigCreateModalComponent,
    EngineStatusModalComponent,
    LightShowPublicPreviewComponent
  ],
  exports: [    
    RequiredIfDirective,
    IgnoreWhiteSpaceDirective,
    DebounceClickDirective
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    NgSelectModule,
    TooltipModule.forRoot(),
    ButtonsModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule.forRoot(AppRoutes),
    FileUploadModule,
    AngularMyDatePickerModule,
    NgxDatatableModule,
    NgxSpinnerModule,
    TabsModule.forRoot(),
    ModalModule.forRoot(),
    ModalDialogModule.forRoot(),
    OrderModule,
    InfiniteScrollModule,
    BrowserAnimationsModule,
    ClipboardModule,
    Ng2ImgMaxModule,
    ColorPickerModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    MatExpansionModule,
    DndModule,
    NgxIndexedDBModule.forRoot(cueServicesDbConfig)
  ],
  providers: [
    // for services
    AuthService,
    AudioTrackStoreService,
    LightShowTrackService,
    SoundEffectService,
    PhotoService,
    VideoService,
    LightShowService,
    CanDeactivateGuard,
    CouponGuard,
    AuthGuard,
    ReverseAuthGuard,
    MerchantAuthGuard,
    ReverseMerchantAuthGuard,
    UserManagementGuard,
    AdministrationGuard,
    UpnGuard,
    TriviaGuard,
    LightShowGuard,
    SelfieCamGuard,
    CoreEngineGuard,
    Cue5050Guard,
    PledgePerWinGuard,
    UserService,
    DatePipe,
    OrganizationService,
    MessageService,
    LightShowService,
    AudioPlayerService,
    S3Service,
    PaymentService,
    TriviaService,
    DialogService,
    EngineService,
    JWTTokenService,
    MerchantService,
    VendorService,
    Cue5050Service,
    StationService,
    OperatorService,
    Cue5050SiteService,
    QRCodeService,
    FundraisingReportService,
    PrinterService,
    PledgePerWinService,
    PledgePerWinSiteService,
    CueAppService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DebugHttpInterceptor,
      multi: true
    },
    SelfieCamService,
    ProductService,
    CouponService,
    UPNService,
    CoreEngineService,
    VenueService,
    NetworkSyncService,
    ScheduledEventService,
    { provide: ErrorHandler, useClass: SentryErrorHandler },
    SentryErrorHandler,
    CurrencyPipe,
    InactivityMonitorService,
    FileDownloadService
  ],
  schemas: [NO_ERRORS_SCHEMA, CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent]
})
export class AppModule { }
